// CRM axios client — separate instance from TG's. baseURL points to /api/v1/crm.
// Auto-attaches the TG JWT from localStorage (single source of truth: 'token').
import axios from "axios";

export const crmApi = axios.create({
  baseURL: "/api/v1/crm",
  timeout: 15000,
});

crmApi.interceptors.request.use((config) => {
  // CrmGlobalProviders is hoisted app-wide (for the floating call widget), so
  // its bootstrap queries fire for EVERY user — including orgs with CRM off,
  // which used to spray retried 403s into the console on every page. When the
  // org profile is loaded and definitively says CRM is off, answer locally
  // with the same shape the backend would send — zero network. While the
  // profile is still unknown we let the request through (the backend is the
  // authority), so CRM orgs on a cold load are never blocked.
  const prof = typeof window !== "undefined" ? window.__orgProfile : null;
  // Cold loads mount the providers BEFORE the org profile arrives, so we also
  // remember the backend's last definitive "CRM off" answer for the session.
  // The profile remains the authority: the moment it says enabled, the flag
  // clears and requests flow again.
  let sessionOff = false;
  try {
    if (prof && prof.crmEnabled === true) sessionStorage.removeItem("tg.crmDisabled");
    else sessionOff = sessionStorage.getItem("tg.crmDisabled") === "1";
  } catch (_e) { /* storage unavailable — fall through to the profile check */ }
  if ((prof && prof.crmEnabled !== true) || (!prof && sessionOff)) {
    const err = new Error("CRM is not enabled for your organization");
    err.config = config;
    err.response = { status: 403, data: { error: "crm_not_enabled", local: true } };
    return Promise.reject(err);
  }
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

crmApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      // Token bad/expired — let TG's existing auth flow handle redirect
    }
    if (err.response?.data?.error === "crm_not_enabled" && !err.response?.data?.local) {
      // Definitive backend answer — remember it so the next cold load doesn't
      // spray bootstrap 403s before the org profile arrives.
      try { sessionStorage.setItem("tg.crmDisabled", "1"); } catch (_e) { /* fine */ }
    }
    return Promise.reject(err);
  }
);
